import React, { useState, useEffect, useMemo } from "react";
import "../../styles/Tickets.css";
import koo from '../../assets/koo.png';
import ServiceSection from "./ServiceSectionTickets";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { useNavigate } from "react-router-dom";
import { useCart } from "../CartContext";

const Tickets = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
          const { cart, setCart, cartCount, setCartCount } = useCart();
  
  const [categories, setCategories] = useState({});
  const [translatedTickets, setTranslatedTickets] = useState({});
  const [activeContent, setActiveContent] = useState("tout");
  const { incrementCartCount, addToCart } = useOutletContext();
  const lang = localStorage.getItem('language') || 'FRA';
  const newlang = lang === 'FRA' ? 'FR' : lang === '' ? 'FR' :  lang === 'ENG' ? 'EN' : lang === 'ESP' ? 'ES' : 'PT';

  const translateBatch = async (texts, targetLanguage) => {
    if (targetLanguage === 'FR') return texts;
    const apiKey = process.env.REACT_APP_DEEPL_API_KEY;
    const url = process.env.REACT_APP_DEEPL_URL;
  
    const params = new URLSearchParams();
    params.append("auth_key", apiKey);
    texts.forEach((text) => params.append("text", text));
    params.append("target_lang", targetLanguage);
  
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params,
      });
  
      const data = await response.json();
  
      if (!data.translations) {
        throw new Error(`Erreur API : ${data.message}`);
      }
  
      return data.translations.map((translation) => translation.text);
    } catch (error) {
      console.error("Erreur de traduction en lot :", error);
      return texts; // En cas d'erreur, on retourne les textes originaux
    }
  };
  

  const fetchCategories = async () => {
    try {
      const response = await fetch(process.env.REACT_APP_BASE_URL + '/api/shop/tickets', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_KEY,
        },
      });
      if (!response.ok) {
        if (response.status === 500) {
          // Redirection vers la page 500
          navigate("/500");
        }
        throw new Error(`Erreur HTTP : ${response.status}`);
      }

      const data = await response.json();
      setCategories(data);

      // Traduction des catégories et tickets en un seul appel
      const textsToTranslate = Object.entries(data).flatMap(([_, categoryData]) => [
        categoryData.name,
        ...categoryData.tickets.flatMap((ticket) => [ticket.name, ticket.description]),
      ]);

      const translations = await translateBatch(textsToTranslate, newlang);

      // Reconstruire les catégories et tickets traduits
      let index = 0;
      const translatedData = Object.entries(data).reduce((acc, [categoryName, categoryData]) => {
        acc[categoryName] = {
          ...categoryData,
          name: translations[index++], // Nom de la catégorie
          tickets: categoryData.tickets.map((ticket) => ({
            ...ticket,
            name: translations[index++],
            description: translations[index++],
          })),
        };
        return acc;
      }, {});

      setTranslatedTickets(translatedData);
    } catch (error) {
      console.error("Erreur lors de la récupération des catégories :", error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [newlang]);

  const isActive = (content) => activeContent === content;

  const allTickets = useMemo(() => {
    return Object.values(translatedTickets)
      .flatMap((categoryData) => categoryData.tickets)
      .filter((ticket, index, self) => self.findIndex(t => t.id === ticket.id) === index);
  }, [translatedTickets]);

  return (
    <div className="ticket-container">
      <div className="ticketext">{t('ticket.title')}</div>
      <header className="ticket-header">
        <nav>
          <ul>
            <li className={isActive("tout") ? "active" : ""} onClick={() => setActiveContent("tout")}>
              {t('ticket.header.all')}
            </li>
            {Object.entries(translatedTickets).map(([categoryName, categoryData]) => (
              <li
                key={categoryData.id}
                className={isActive(categoryName) ? "active" : ""}
                onClick={() => setActiveContent(categoryName)}>
                {categoryData.name.toUpperCase()}
              </li>
            ))}
          </ul>
        </nav>
      </header>
      <div className="ticket-content">
        <div className="ticket-containe">
          {activeContent === "tout" ? (
            <div className="all-categories">
              <ToastContainer position="top-right" autoClose={800} />
              {allTickets.map((ticket) => (
                <div className="ticket" key={ticket.id}>
                  <div className="ticket-image">
                    <img
                      src={`${process.env.REACT_APP_BASE_URL}/${ticket.image}`}
                      alt={ticket.name}
                      className={`logo-ticket ${ticket.quantity === 0 ? 'grayscale' : ''}`}
                      loading="lazy"
                    />
                    {ticket.quantity === 0 && (
                      <img src={koo} alt="Sold Out" className="sold-out-overlay" />
                    )}
                  </div>
                  <div className="ticket-info">
                    <h3>{ticket.name}</h3>
                    <p>{ticket.description}</p>
                  </div>
                  <div className="ticket-price">
                    <span>{ticket.price.toLocaleString()} FCFA</span>
                    <button
                      className={`add-to-cart-button ${ticket.quantity === 0 ? 'disabled' : ''}`}
                      onClick={() =>
                        ticket.quantity > 0 &&
                        addToCart(
                          "ticket-" + ticket.id,
                          ticket.name,
                          ticket.price,
                          ticket.type,
                          ticket.image,
                          ticket.description,
                          ticket.quantity
                        )
                      }
                      disabled={ticket.quantity === 0}>
                      <i className="fa fa-shopping-cart"></i> {t("ticket.add_to_cart")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="category-tickets">
              <ToastContainer position="top-right" autoClose={800} />
              {translatedTickets[activeContent]?.tickets.map((ticket) => (
                <div className="ticket" key={ticket.id}>
                  <div className="ticket-image">
                    <img
                      src={`${process.env.REACT_APP_BASE_URL}/${ticket.image}`}
                      alt={ticket.name}
                      className={`logo-ticket ${ticket.quantity === 0 ? 'grayscale' : ''}`}
                      loading="lazy"
                    />
                    {ticket.quantity === 0 && (
                      <img src={koo} alt="Sold Out" className="sold-out-overlay" />
                    )}
                  </div>
                  <div className="ticket-info">
                    <h3>{ticket.name}</h3>
                    <p>{ticket.description}</p>
                  </div>
                  <div className="ticket-price">
                    <span>{ticket.price.toLocaleString()} FCFA</span>
                    <button
                      className={`add-to-cart-button ${ticket.quantity === 0 ? 'disabled' : ''}`}
                      onClick={() =>
                        ticket.quantity > 0 &&
                        addToCart(
                          "ticket-" + ticket.id,
                          ticket.name,
                          ticket.price,
                          ticket.type,
                          ticket.image,
                          ticket.description,
                          ticket.quantity
                        )
                      }
                      disabled={ticket.quantity === 0}>
                      <i className="fa fa-shopping-cart"></i> {t('ticket.add_to_cart')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div>
      <ServiceSection />
      </div>
    </div>
  );
};

export default Tickets;
